import axios from 'axios';
import dayjs from 'dayjs';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { CATEGORY_LIST } from '../constant/index';
import AddOptions from '../molecules/AddOptions';
import AdditionalProducts from '../molecules/AddtionalProducts';
import InputFiles from '../molecules/InputFiles';
import InputSelect from '../molecules/InputSelect';
import InputText from '../molecules/InputText';
import InputTextarea from '../molecules/InputTextarea';
import { KRNationalities } from '../nationalities';
import AdminRepository, { API_ROOT_URL } from '../repositories/AdminRepository';

/** 제출 실패 시 오류 목록을 화면에 표시하는 컴포넌트 */
function ValidationSummary({ errors, onClose }) {
  if (!errors || errors.length === 0) return null;

  return (
    <div className="w-full bg-red-900 bg-opacity-80 border border-red-500 rounded-lg p-4 mb-4">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-red-300 font-bold mb-2">⚠️ 아래 항목을 확인해주세요</p>
          <ul className="list-disc list-inside space-y-1">
            {errors.map((err, idx) => (
              <li key={idx} className="text-red-200 text-sm">
                <span className="font-semibold">[{err.label}]</span> {err.message}
              </li>
            ))}
          </ul>
        </div>
        <button
          onClick={onClose}
          className="text-red-300 hover:text-white ml-4 text-xl font-bold"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

const ProductEnroll = forwardRef(
  ({ setShowModal, refreshProduct, KRWPerUSD, isAdmin }, ref) => {
    const [title, setTitle] = useState('');
    const [categoryCode, setCategoryCode] = useState('');
    const [countryCode, setCountryCode] = useState('KR');
    const [attachmentList, setAttachmentList] = useState([]);
    const [descriptionImageList, setDescriptionImageList] = useState([]);
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [discountPrice, setDiscountPrice] = useState('');
    const [shipmentCost, setShipmentCost] = useState('');
    const [shipmentCostUS, setShipmentCostUS] = useState(
      String(Math.round(20 * KRWPerUSD)),
    );
    const [isAvailableFreeShippingKR, setIsAvailableFreeShippingKR] =
      useState(false);
    const [
      lowestOrderPriceForFreeDeliveryKR,
      setLowestOrderPriceForFreeDeliveryKR,
    ] = useState('');
    const [isAvailableFreeShippingUS, setIsAvailableFreeShippingUS] =
      useState(false);
    const [
      lowestOrderPriceForFreeDeliveryUS,
      setLowestOrderPriceForFreeDeliveryUS,
    ] = useState('');

    const [isNoLimitNumberToSale, setIsNoLimitNumberToSale] = useState(false);
    const [availableNumberToSale, setAvailableNumberToSale] = useState('');

    // additional product
    const [additionalProducts, setAdditionalProducts] = useState({});
    const [additionalOptions, setAdditionalOptions] = useState({});

    const [isAvailableToSale, setIsAvailableToSale] = useState(true);

    // validation
    const [isValidTitle, setIsValidTitle] = useState(false);
    const [isValidCategory, setIsValidCategory] = useState(false);
    const [isValidCountry, setIsValidCountry] = useState(true);
    const [isValidAttachments, setIsValidAttachments] = useState(false);
    const [isValidDescription, setIsValidDescription] = useState(false);
    const [isValidPrice, setIsValidPrice] = useState(false);
    const [isValidShipmentCostKR, setIsValidShipmentCostKR] = useState(true);
    const [isValidShipmentCostUS, setIsValidShipmentCostUS] = useState(true);

    const [isDiscountEnabled, setIsDiscountEnabled] = useState(false);
    const [isPromotion, setIsPromotion] = useState(false);
    const [promotionStartDate, setPromotionStartDate] = useState(new Date());
    const [promotionEndDate, setPromotionEndDate] = useState(new Date());
    const [isRefundable, setIsRefundable] = useState(false);

    const [productImageCount, setProductImageCount] = useState(0);
    const [productDetailImageCount, setProductDetailImageCount] = useState(0);

    // 유효성 오류 목록 (제출 시 표시)
    const [validationErrors, setValidationErrors] = useState([]);

    // 각 필드 ref (오류 시 자동 스크롤용)
    const titleRef = useRef(null);
    const categoryRef = useRef(null);
    const attachmentsRef = useRef(null);
    const descriptionRef = useRef(null);
    const priceRef = useRef(null);
    const shipmentRef = useRef(null);

    useImperativeHandle(ref, () => ({
      handleSubmit,
    }));

    const handleSubmit = () => {
      // 가격/배송비: 빈 문자열이면 미입력, 숫자(0 포함)면 유효
      const isPriceValid = price !== '' && !Number.isNaN(+price);
      const isShipmentKRValid = shipmentCost !== '' && !Number.isNaN(+shipmentCost);

      // 오류 항목 수집
      const errors = [];
      if (!isValidTitle) errors.push({ label: '상품 이름', message: '필수 입력값입니다.' });
      if (!isValidCategory) errors.push({ label: '카테고리', message: '카테고리를 선택해주세요.' });
      if (!isValidCountry) errors.push({ label: '상품 판매국', message: '판매국을 선택해주세요.' });
      if (!isValidAttachments) errors.push({ label: '상품 대표 이미지', message: '이미지를 1장 이상 첨부해주세요.' });
      if (!isValidDescription) errors.push({ label: '상품 상세 설명', message: '필수 입력값입니다.' });
      if (!isPriceValid) errors.push({ label: '가격', message: '가격을 입력해주세요. (0 이상의 숫자)' });
      if (!isShipmentKRValid) errors.push({ label: '배송비(국내)', message: '배송비를 입력해주세요. (무료배송은 0 입력)' });

      if (errors.length > 0) {
        setValidationErrors(errors);

        // 첫 번째 오류 필드로 스크롤
        let firstErrorRef = shipmentRef;
        if (!isValidTitle) firstErrorRef = titleRef;
        else if (!isValidCategory) firstErrorRef = categoryRef;
        else if (!isValidAttachments) firstErrorRef = attachmentsRef;
        else if (!isValidDescription) firstErrorRef = descriptionRef;
        else if (!isPriceValid) firstErrorRef = priceRef;

        firstErrorRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      setValidationErrors([]);

      const formData = new FormData();

      const options = {
        checks: Object.keys(additionalProducts).map((key) => ({
          name: additionalProducts[key].name,
          addition: additionalProducts[key].addition,
        })),
        lists: Object.keys(additionalOptions).map((key) => ({
          name: additionalOptions[key].name,
          items: additionalOptions[key].items,
        })),
      };

      formData.append('title', title);
      formData.append('description', description);
      formData.append('countryCode', countryCode);
      formData.append('price', price);
      formData.append('discountPrice', discountPrice || 0);
      formData.append('shipmentCost', shipmentCost);
      formData.append('shipmentCostUS', shipmentCostUS || 0);
      formData.append('isAvailableFreeShippingKR', isAvailableFreeShippingKR);
      formData.append(
        'lowestOrderPriceForFreeDeliveryKR',
        lowestOrderPriceForFreeDeliveryKR || 0,
      );
      formData.append('isAvailableFreeShippingUS', isAvailableFreeShippingUS);
      formData.append(
        'lowestOrderPriceForFreeDeliveryUS',
        lowestOrderPriceForFreeDeliveryUS || 0,
      );

      for (let i = 0; i < attachmentList.length; i++) {
        formData.append('productImage', attachmentList[i]);
      }

      for (let i = 0; i < descriptionImageList.length; i++) {
        formData.append('descriptionImage', descriptionImageList[i]);
      }

      formData.append('categoryCode', categoryCode);
      formData.append(
        'availableNumberToSale',
        isNoLimitNumberToSale ? (availableNumberToSale || 0) : -1,
      );
      formData.append('options', JSON.stringify(options));
      formData.append('requesterId', AdminRepository.requesterId);
      formData.append('requesterToken', AdminRepository.requesterToken);
      formData.append('isAvailableToSale', isAvailableToSale);

      formData.append('isPromotion', isPromotion);

      if (isPromotion) {
        formData.append('promotionStartDate', promotionStartDate);
        formData.append('promotionEndDate', promotionEndDate);
        formData.append('eventType', isRefundable ? 'refund' : '');
      }

      axios
        .post(`${API_ROOT_URL}/products`, formData, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
            version: 20221210,
            os: 'webadmin',
            token: AdminRepository.requesterToken,
          },
        })
        .then((res) => {
          if (res instanceof Error) {
            alert('상품 등록에 실패했습니다.');
            return;
          }

          alert('상품 등록이 완료되었습니다.');
          setShowModal(false);
          refreshProduct();
        })
        .catch((error) => {
          if (error.response) {
            const data = error.response.data;
            const errorsObj = data?.errors || {};
            const serverMsg = Object.keys(errorsObj).length > 0
              ? Object.values(errorsObj)
                  .map((e) => `"${e.path || e.name}": ${e.message || e.kind}`)
                  .join('\n')
              : (data?.message || '알 수 없는 오류');
            alert(
              `상품 등록에 실패했습니다.\n\n오류 내용:\n${serverMsg}\n\n입력 내용을 다시 확인하거나 관리자에게 문의해주세요.`,
            );
          } else {
            alert('서버와 통신 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
          }
        });
    };

    return (
      <div className="">
        <ValidationSummary
          errors={validationErrors}
          onClose={() => setValidationErrors([])}
        />

        <div ref={titleRef}>
          <InputText
            label="상품 이름"
            inputId="title"
            placeholder="상품 이름을 입력해주세요"
            value={title}
            setValue={setTitle}
            required
            isValid={isValidTitle}
            setIsValid={setIsValidTitle}
          />
        </div>

        <div ref={categoryRef}>
          <InputSelect
            inputId="category"
            label="카테고리"
            placeholder="카테고리를 선택해주세요"
            list={CATEGORY_LIST}
            required
            value={categoryCode}
            setValue={setCategoryCode}
            isValid={isValidCategory}
            setIsValid={setIsValidCategory}
          />
          <InputSelect
            inputId="countryCode"
            label="상품 판매국"
            placeholder="상품 판매국을 선택해주세요"
            list={KRNationalities}
            required
            value={countryCode}
            setValue={setCountryCode}
            isValid={isValidCountry}
            setIsValid={setIsValidCountry}
          />
        </div>

        <div ref={attachmentsRef}>
          <InputFiles
            label="상품 이미지"
            inputId="productImages"
            placeholder={`상품 대표 이미지를 첨부해주세요 (${productImageCount}/10)`}
            required
            value={attachmentList}
            setValue={setAttachmentList}
            isValid={isValidAttachments}
            setIsValid={setIsValidAttachments}
            setImageCount={setProductImageCount}
          />
          <InputFiles
            label="상품 설명 이미지"
            inputId="productDescribeImages"
            placeholder={`상품 설명 이미지를 첨부해주세요 (${productDetailImageCount}/15)`}
            value={descriptionImageList}
            setValue={setDescriptionImageList}
            setImageCount={setProductDetailImageCount}
          />
        </div>

        <div ref={descriptionRef}>
          <InputTextarea
            label="상품 상세 설명"
            inputId="description"
            placeholder="상품에 대해 추가적으로 설명할 내용을 입력해주세요"
            value={description}
            setValue={setDescription}
            required
            isValid={isValidDescription}
            setIsValid={setIsValidDescription}
          />
        </div>

        <div ref={priceRef}>
          <InputText
            label="가격"
            inputId="price"
            placeholder="상품 가격을 입력해주세요 (무료는 0)"
            value={price}
            setValue={setPrice}
            required
            isValid={isValidPrice}
            setIsValid={setIsValidPrice}
            inputMode="numeric"
            customType="price"
          />
        </div>

        <div className="flex items-center justify-center mb-2">
          <span className="text-striver font-semibold mr-2">할인여부</span>
          <input
            type="checkbox"
            className="cursor-pointer accent-giver w-4 h-4 relative rounded-full"
            checked={isDiscountEnabled}
            onChange={(e) => {
              setIsDiscountEnabled(e.target.checked);
            }}
          />
        </div>

        {isDiscountEnabled ? (
          <InputText
            label="할인가격"
            inputId="discountPrice"
            placeholder="상품 할인 가격을 입력해주세요"
            value={discountPrice.toString()}
            setValue={setDiscountPrice}
            inputMode="numeric"
            customType="price"
            required
          />
        ) : null}

        <div ref={shipmentRef}>
          <InputText
            label="배송비(국내)"
            inputId="shipmentCostKR"
            placeholder="배송비를 입력해주세요 (무료배송은 0)"
            value={shipmentCost}
            setValue={setShipmentCost}
            inputMode="numeric"
            required
            isValid={isValidShipmentCostKR}
            setIsValid={setIsValidShipmentCostKR}
            customType="price"
          />
        </div>

        <div className="flex items-center justify-center mb-2">
          <span className="text-striver font-semibold mr-2">
            무료배송 가능여부 (국내)
          </span>
          <input
            type="checkbox"
            className="cursor-pointer accent-giver w-4 h-4 relative rounded-full"
            checked={isAvailableFreeShippingKR}
            onChange={(e) => {
              setIsAvailableFreeShippingKR(e.target.checked);
            }}
          />
        </div>

        {isAvailableFreeShippingKR ? (
          <InputText
            label="무료배송 최소금액(국내)"
            inputId="freeShipmentKR"
            placeholder="무료배송 최소 주문금액을 입력해주세요"
            value={lowestOrderPriceForFreeDeliveryKR}
            setValue={setLowestOrderPriceForFreeDeliveryKR}
            required
            inputMode="numeric"
            customType="price"
          />
        ) : null}

        <InputText
          label="배송비(해외)"
          inputId="shipmentCostUS"
          placeholder="해외 배송비를 입력해주세요"
          value={shipmentCostUS}
          setValue={setShipmentCostUS}
          inputMode="numeric"
          isValid={isValidShipmentCostUS}
          setIsValid={setIsValidShipmentCostUS}
          customType="price"
        />

        <div className="flex items-center justify-center mb-2">
          <span className="text-striver font-semibold mr-2">
            무료배송 가능여부 (해외)
          </span>
          <input
            type="checkbox"
            className="cursor-pointer accent-giver w-4 h-4 relative rounded-full"
            checked={isAvailableFreeShippingUS}
            onChange={(e) => {
              setIsAvailableFreeShippingUS(e.target.checked);
            }}
          />
        </div>

        {isAvailableFreeShippingUS ? (
          <InputText
            label="무료배송 최소금액(해외)"
            inputId="freeShipmentUS"
            placeholder="해외 무료배송 최소 주문금액을 입력해주세요"
            value={lowestOrderPriceForFreeDeliveryUS}
            setValue={setLowestOrderPriceForFreeDeliveryUS}
            required
            inputMode="numeric"
            customType="price"
          />
        ) : null}

        <div className="flex items-center justify-center mb-2">
          <span className="text-striver font-semibold mr-2">상품 재고 직접 입력</span>
          <input
            type="checkbox"
            className="cursor-pointer accent-giver w-4 h-4 relative rounded-full"
            checked={isNoLimitNumberToSale}
            onChange={(e) => {
              setIsNoLimitNumberToSale(e.target.checked);
            }}
          />
        </div>

        {isNoLimitNumberToSale ? (
          <InputText
            label="상품 재고 개수"
            inputId="availableNumberToSale"
            placeholder="판매할 상품의 재고를 입력하세요"
            value={availableNumberToSale}
            setValue={setAvailableNumberToSale}
            required
            isValid
            inputMode="numeric"
          />
        ) : null}

        <span className="text-explorer font-semibold text-base">
          선택항목 추가
        </span>
        <div className="flex flex-col mt-2">
          <AdditionalProducts
            productInputs={additionalProducts}
            setProductInputs={setAdditionalProducts}
          />
          <AddOptions
            optionInputs={additionalOptions}
            setOptionInputs={setAdditionalOptions}
          />
        </div>

        {isAdmin ? (
          <>
            <div className="flex items-center justify-center mb-2">
              <span className="text-striver font-semibold mr-2">
                프로모션 상품
              </span>
              <input
                type="checkbox"
                className="cursor-pointer accent-giver w-4 h-4 relative rounded-full"
                checked={isPromotion}
                onChange={(e) => {
                  setIsPromotion(e.target.checked);
                  setIsRefundable(e.target.checked);
                }}
              />
            </div>
            <div className="flex items-center justify-center mb-2">
              <span className="text-striver font-semibold mr-2">100% 환불</span>
              <input
                type="checkbox"
                className="cursor-pointer accent-giver w-4 h-4 relative rounded-full"
                checked={isRefundable}
                onChange={(e) => {
                  setIsRefundable(e.target.checked);
                }}
              />
            </div>

            {isPromotion ? (
              <div className="flex flex-row justify-evenly">
                <div className="flex flex-col items-center">
                  <span className="text-pioneer">시작일</span>
                  <input
                    className="mt-1 px-2 py-1 rounded-md"
                    type="date"
                    onChange={(e) => setPromotionStartDate(e.target.value)}
                    value={dayjs(promotionStartDate).format('YYYY-MM-DD')}
                  />
                </div>

                <div className="flex flex-col items-center">
                  <span className="text-pioneer">종료일</span>
                  <input
                    className="mt-1 px-2 py-1 rounded-md"
                    type="date"
                    onChange={(e) => setPromotionEndDate(e.target.value)}
                    value={dayjs(promotionEndDate).format('YYYY-MM-DD')}
                  />
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    );
  },
);

ProductEnroll.displayName = 'ProductEnroll';
export default ProductEnroll;
